import qs from 'qs';
import {
  setAuth, clearAuth, setUser, changeTFAState, setPasswordExpired, setAuthUserPartnerAccesses,
} from 'app/auth/slices';
import { baseApi } from 'services/baseApi';
import i18n from 'services/i18n';
import { LocalStorageService } from 'services';
import { changeEndedStatus, setErrorMessage } from 'app/settings/slices';
import {
  LoginRequest,
  User,
  ResetPasswordParams,
  PasswordRequest,
  ChangePasswordRequest,
  ExpiredPassword,
  UserPartnerAccessesRequest,
  PartnerAccesses,
  AuthTokenResponse,
  LaunchToken,
} from 'app/auth/types';
import { ApiError, ErrorResponse } from 'types/error';
import { AppConstants } from 'constants/index';
import { isJSON } from 'utils/utils';
import { getError } from 'utils/helpers';
import { Meta } from 'types';

const t = i18n.getFixedT('en');

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthTokenResponse, LoginRequest>({
      query: (data) => {
        const formData = qs.stringify({ ...data, grant_type: 'password', client_id: 'admin_client' });

        const { Admin, Account, Token } = AppConstants.api;
        return {
          url: [Admin, Account, Token],
          method: AppConstants.methods.POST,
          data: formData,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          meta: {
            skipErrorHandler: true,
          },
        };
      },
      async onQueryStarted(arg: LoginRequest, { dispatch, queryFulfilled }): Promise<void> {
        try {
          const { data } = await queryFulfilled;
          dispatch(setAuth());
          LocalStorageService.set('auth', data);
        } catch (err: unknown) {
          const apiError = err as ApiError;
          const msg = apiError.error?.message;

          if (msg && isJSON(msg)) {
            const { response } = JSON.parse(msg);
            const error = getError({ response }) as ErrorResponse;
            if (error.errors[0].errorMessage === t('notification:TFARequired')) {
              dispatch(changeTFAState(true));
            } else if (error.errors[0].errorMessage === t('notification:passwordExpired')) {
              dispatch(setPasswordExpired(true));
            } else {
              const responseData = response?.data;
              const errorData = Array.isArray(responseData) ? responseData[0] : responseData;

              dispatch(
                setErrorMessage({
                  errors: [
                    {
                      errorMessage: errorData?.errorMessage,
                      errorCode: errorData?.errorCode,
                    },
                  ],
                }),
              );
            }
          }
        }
      },
    }),
    logout: builder.mutation<void, void>({
      query: () => {
        const { Admin, Account, Logout } = AppConstants.api;
        return {
          url: [Admin, Account, Logout],
          method: AppConstants.methods.POST,
        };
      },
    }),
    getAuthUser: builder.query<User, void>({
      query: () => {
        const { Admin, Account, Details } = AppConstants.api;
        return {
          url: [Admin, Account, Details],
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
          LocalStorageService.set('user', data);
        } catch {
          LocalStorageService.remove('auth');
          dispatch(clearAuth());
        }
      },
    }),
    forgotPassword: builder.mutation<void, PasswordRequest>({
      query: (params) => {
        const { Admin, Account, ForgotPassword } = AppConstants.api;
        return {
          url: [Admin, Account, ForgotPassword],
          method: AppConstants.methods.POST,
          params,
          headers: {
            Authorization: '',
          },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(changeEndedStatus(true));
      },
    }),
    resetPassword: builder.mutation<void, ResetPasswordParams>({
      query: ({ data, text }) => {
        const { Admin, Account, ResetPassword } = AppConstants.api;
        return {
          url: [Admin, Account, ResetPassword],
          method: AppConstants.methods.POST,
          data,
          successMessage: t('notification:generalMessage', {
            model: t('password'),
            action: text,
            name: '',
          }),
        };
      },

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(changeEndedStatus(true));
        LocalStorageService.clear();
      },
    }),
    resendTFA: builder.mutation<void, PasswordRequest>({
      query: (params) => {
        const { Admin, Account, ResendTFA } = AppConstants.api;

        return {
          url: [Admin, Account, ResendTFA],
          method: AppConstants.methods.POST,
          params,
          successMessage: t('notification:resendTFASuccess'),
        };
      },
      async onQueryStarted(arg: PasswordRequest, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(changeEndedStatus(true));
        LocalStorageService.clear();
      },
    }),
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (data) => {
        const { Admin, Account, ChangePassword } = AppConstants.api;

        return {
          url: [Admin, Account, ChangePassword],
          method: AppConstants.methods.POST,
          data,
          successMessage: t('notification:generalMessage', { model: t('password'), action: t('notification:slUpdated'), name: '' }),
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(changeEndedStatus(true));
      },
    }),
    changeExpiredPassword: builder.mutation<void, ExpiredPassword>({
      query: (data) => {
        const { Admin, Account, ChangeExpiredPassword } = AppConstants.api;

        return {
          url: [Admin, Account, ChangeExpiredPassword],
          method: AppConstants.methods.POST,
          data,
          meta: {
            successMessage: t('notification:generalMessage', {
              model: t('password'),
              action: t('notification:slUpdated'),
              name: '',
            }),
          },
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(changeEndedStatus(true));
      },
    }),
    getAuthUserPartnerAccesses: builder.query<PartnerAccesses, UserPartnerAccessesRequest>({
      query: ({ includeMainPartner }) => {
        const { Admin, Account, Partners } = AppConstants.api;

        return {
          url: [Admin, Account, Partners, includeMainPartner],
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(setAuthUserPartnerAccesses(data));
      },
    }),
    generateLaunchToken: builder.mutation<LaunchToken, void>({
      query: () => {
        const { Admin, Account, GenerateLaunchToken } = AppConstants.api;
        return {
          method: AppConstants.methods.POST,
          url: [Admin, Account, GenerateLaunchToken],
        };
      },
    }),
  }),
});

export const { useGenerateLaunchTokenMutation } = authApi;
